import { useState, type FormEvent } from 'react';
import { api, setCsrfToken, RequestFailed } from '../lib/api';
import { messageFor } from '../lib/session';
import { Field, Notice } from './ui';

type Props = { base: string; panelName: string; onDone: () => void };

/**
 * Mirrors MIN_PASSWORD_LENGTH in server/src/services/auth/passwords.ts. The
 * server remains the authority — this only spares someone a round trip to be
 * told their password is short.
 */
const MIN_PASSWORD_LENGTH = 12;

function Shell({ title, blurb, children, foot }: {
  title: string; blurb?: string; children: React.ReactNode; foot?: React.ReactNode;
}) {
  return (
    <div className="auth-shell">
      <div className="auth-card">
        <div className="brand">WEBFORGROWTH</div>
        <h1>{title}</h1>
        {blurb && <p>{blurb}</p>}
        {children}
        {foot && <div className="auth-foot">{foot}</div>}
      </div>
    </div>
  );
}

export function SignIn({ base, panelName, onDone }: Props) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<{ csrfToken: string }>(`${base}/auth/login`, { email, password });
      setCsrfToken(res.csrfToken);
      onDone();
    } catch (err) {
      setError(messageFor(err));
    } finally {
      setBusy(false);
    }
  }

  async function forgot() {
    if (!email) {
      setError('Enter your email address first, then choose Forgot password.');
      return;
    }
    try {
      await api.post(`${base}/auth/forgot`, { email });
    } catch {
      // The endpoint answers the same either way on purpose.
    }
    setResetSent(true);
  }

  return (
    <Shell title={`${panelName} sign in`} blurb={`Signed-in access only. There is no public registration.`}>
      {error && <Notice kind="error">{error}</Notice>}
      {resetSent && (
        <Notice kind="ok">If that address has an account, a reset link is on its way. It expires in an hour.</Notice>
      )}
      <form onSubmit={submit}>
        <Field label="Email address">
          <input type="email" value={email} autoComplete="username" required
                 onChange={(e) => setEmail(e.target.value)} />
        </Field>
        <Field label="Password">
          <input type="password" value={password} autoComplete="current-password" required
                 onChange={(e) => setPassword(e.target.value)} />
        </Field>
        <button type="submit" disabled={busy}>{busy ? 'Checking…' : 'Continue'}</button>
      </form>
      <div className="auth-foot">
        <button type="button" className="ghost small" onClick={forgot}>Forgot password</button>
      </div>
    </Shell>
  );
}

export function EnrollTwoFactor({ base, onDone, required }: Props & { required: boolean }) {
  const [secret, setSecret] = useState<{ secret: string; qr: string } | null>(null);
  const [code, setCode] = useState('');
  const [codes, setCodes] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function begin() {
    setBusy(true);
    setError(null);
    try {
      setSecret(await api.post<{ secret: string; qr: string }>(`${base}/auth/2fa/setup`, {}));
    } catch (err) {
      setError(messageFor(err));
    } finally {
      setBusy(false);
    }
  }

  async function confirm(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<{ csrfToken: string; recoveryCodes: string[] }>(
        `${base}/auth/2fa/confirm`, { code },
      );
      setCsrfToken(res.csrfToken);
      setCodes(res.recoveryCodes);
    } catch (err) {
      setError(messageFor(err));
    } finally {
      setBusy(false);
    }
  }

  if (codes) {
    return (
      <Shell
        title="Save your recovery codes"
        blurb="Each of these works once, if you lose your phone. This is the only time they are shown."
      >
        <div className="codes">{codes.map((c) => <span key={c}>{c}</span>)}</div>
        <button onClick={onDone}>I have saved them</button>
      </Shell>
    );
  }

  if (!secret) {
    return (
      <Shell
        title="Set up two-factor authentication"
        blurb={required
          ? 'Two-factor is required on every staff account, with no exception. It takes about a minute.'
          : 'Strongly recommended. It is the single most effective thing you can do for this account.'}
      >
        {error && <Notice kind="error">{error}</Notice>}
        <button onClick={begin} disabled={busy}>{busy ? 'Preparing…' : 'Begin setup'}</button>
      </Shell>
    );
  }

  return (
    <Shell
      title="Scan this with your authenticator"
      blurb="Use 1Password, Authy, Google Authenticator or similar, then enter the six-digit code it shows."
    >
      {error && <Notice kind="error">{error}</Notice>}
      <img className="qr" src={secret.qr} alt="Two-factor setup QR code" width={240} height={240} />
      <div className="secret-box">{secret.secret}</div>
      <form onSubmit={confirm}>
        <Field label="Six-digit code">
          <input inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]*" maxLength={6}
                 value={code} required onChange={(e) => setCode(e.target.value)} />
        </Field>
        <button type="submit" disabled={busy || code.length < 6}>{busy ? 'Checking…' : 'Confirm'}</button>
      </form>
    </Shell>
  );
}

export function VerifyTwoFactor({ base, onDone }: Props) {
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<{ csrfToken: string }>(`${base}/auth/2fa/verify`, { code });
      setCsrfToken(res.csrfToken);
      onDone();
    } catch (err) {
      setError(messageFor(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Shell
      title="Two-factor code"
      blurb="Enter the code from your authenticator app, or one of your recovery codes."
    >
      {error && <Notice kind="error">{error}</Notice>}
      <form onSubmit={submit}>
        <Field label="Code">
          <input autoComplete="one-time-code" value={code} required autoFocus
                 onChange={(e) => setCode(e.target.value)} />
        </Field>
        <button type="submit" disabled={busy}>{busy ? 'Checking…' : 'Verify'}</button>
      </form>
    </Shell>
  );
}

/** Used for both the invite acceptance and the password reset landing pages. */
export function SetPassword({ base, endpoint, title, blurb, onDone }: {
  base: string;
  endpoint: 'accept-invite' | 'reset';
  title: string;
  blurb: string;
  onDone: () => void;
}) {
  const token = new URLSearchParams(window.location.search).get('token') ?? '';
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirm] = useState('');
  const [reveal, setReveal] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /**
   * Set when the server says the link itself is finished. It replaces the form
   * rather than sitting above it: this page previously answered a used or
   * lapsed link with a red banner over working-looking inputs, so people
   * retyped their password into something that could never succeed.
   */
  const [linkDead, setLinkDead] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const signInHref = base.includes('admin') ? '/admin' : '/portal';

  const tooShort = password.length > 0 && password.length < MIN_PASSWORD_LENGTH;
  const mismatch = confirmPassword.length > 0 && confirmPassword !== password;
  const ready = password.length >= MIN_PASSWORD_LENGTH && password === confirmPassword;

  // A stale failure must not outlive the edit that answers it. Without this,
  // the banner from the last attempt hangs over a form the user has since
  // corrected, and it reads as though the new attempt failed too.
  function change(set: (value: string) => void) {
    return (event: { target: { value: string } }) => {
      if (error) setError(null);
      set(event.target.value);
    };
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Use at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }
    if (password !== confirmPassword) {
      setError('Those two passwords do not match.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<{ csrfToken?: string }>(`${base}/auth/${endpoint}`, { token, password });
      if (res.csrfToken) setCsrfToken(res.csrfToken);
      if (endpoint === 'accept-invite') {
        onDone();
      } else {
        setDone(true);
      }
    } catch (err) {
      if (err instanceof RequestFailed && err.error.code === 'invalid_token') {
        setLinkDead(err.error.message);
      } else {
        setError(messageFor(err));
      }
    } finally {
      setBusy(false);
    }
  }

  if (!token) {
    return <Shell title="That link is incomplete" blurb="Ask for a fresh link — this one is missing its token." >{null}</Shell>;
  }

  if (linkDead) {
    return (
      <Shell title="That link is no longer valid" blurb={linkDead}>
        <a className="btn" href={signInHref}>Go to sign in</a>
      </Shell>
    );
  }

  if (done) {
    return (
      <Shell title="Password updated" blurb="Every other signed-in session has been ended as a precaution.">
        <a className="btn" href={signInHref}>Sign in</a>
      </Shell>
    );
  }

  return (
    <Shell title={title} blurb={blurb}>
      {error && <Notice kind="error">{error}</Notice>}
      <form onSubmit={submit} noValidate>
        <Field
          label="New password"
          hint={
            tooShort
              ? `${MIN_PASSWORD_LENGTH - password.length} more character${
                  MIN_PASSWORD_LENGTH - password.length === 1 ? '' : 's'
                } needed.`
              : `At least ${MIN_PASSWORD_LENGTH} characters. Checked against known breach lists.`
          }
        >
          <input type={reveal ? 'text' : 'password'} autoComplete="new-password" value={password}
                 aria-invalid={tooShort || undefined}
                 onChange={change(setPassword)} />
        </Field>
        <Field label="Confirm password" hint={mismatch ? 'These do not match yet.' : undefined}>
          <input type={reveal ? 'text' : 'password'} autoComplete="new-password" value={confirmPassword}
                 aria-invalid={mismatch || undefined}
                 onChange={change(setConfirm)} />
        </Field>
        {/* Outside Field, whose <label> would forward the click to the input.
            Typing twelve-plus characters twice, unseen, on a phone is where
            the mismatches come from. */}
        <label className="reveal-toggle">
          <input type="checkbox" checked={reveal} onChange={(e) => setReveal(e.target.checked)} />
          <span>Show passwords</span>
        </label>
        <button type="submit" disabled={busy || !ready}>{busy ? 'Saving…' : 'Set password'}</button>
      </form>
    </Shell>
  );
}
